/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { NavigateFunction, Params } from "react-router"
import { useNavigate } from "react-router"

import { hasOrgAdminAccess, hasSuperAdminAccess } from "@/features/access-rights/access-rights"
import { ApkDetailViewModel } from "@/features/apk-detail/apk-detail-viewmodel"
import type { AppState } from "@/features/app/app-state"
import {
    getAppTypeIdFromParams,
    getFlagsFromUrl,
    getPageFromUrl,
    getStatusFromUrl
} from "@/features/application/application-url-utils"
import { ApplicationViewModel } from "@/features/application/application-viewmodel"
import { ApplicationDetailViewModel } from "@/features/application-detail/application-detail-viewmodel"
import { ApplicationSearchViewmodel } from "@/features/application-search/application-search-viewmodel"
import { ApplicationTypeViewModel } from "@/features/application-type/application-type-viewmodel"
import { OrganizationViewModel } from "@/features/organization/organization-viewmodel"
import { SessionViewModel } from "@/features/session/session-view-model"
import { UserAppTypeViewModel } from "@/features/user/user-apptype-viewmodel"
import { UserDetailViewModel } from "@/features/user/user-detail-view-model"
import { UserListViewModel } from "@/features/user/user-list-viewmodel"
import { LoggerViewModel } from "@/logger/logger-viewmodel"
import { AppRoutes } from "@/routes/routes"
import { forceRedirect404 } from "@/utils/force-redirect"
import { RouteLoading, useRouteEffect } from "@/utils/use-route-effect"
import type { ViewModelContainer } from "@/viewmodel/viewmodel"
import { useAppState, useViewModelContainer } from "./app-hooks"

/**
 * Runs route-specific data loading when a route becomes active.
 *
 * Renders nothing; it runs inside the app context so it can access view models,
 * app state, and navigation.
 */
export const RouteChangeEffect = (): null => {
    const provider = useViewModelContainer()
    const appState = useAppState()
    const navigate = useNavigate()

    useRouteEffect(
        AppRoutes.Dashboard,
        () => {
            return loadDashboard(provider, appState)
        },
        // loadDashboard owns the loading state.
        { loading: RouteLoading.None }
    )

    useRouteEffect(
        AppRoutes.AllOrganizations,
        () => {
            // Refresh on every visit so changes made elsewhere are reflected.
            return loadOrganizations(provider, appState, { refresh: true })
        },
        // The table owns its loading state and shows skeleton rows.
        { loading: RouteLoading.None }
    )

    useRouteEffect(AppRoutes.AllApplicationTypes, async () => {
        await Promise.all([
            loadOrganizations(provider, appState),
            provider.getViewModel(ApplicationTypeViewModel).getAllAppTypes()
        ])
    })

    useRouteEffect(
        AppRoutes.ApplicationTypeDetail,
        (params) => {
            return loadApplicationTypeDetail(provider, appState, getAppTypeIdFromParams(params))
        },
        // loadApplicationTypeDetail owns the loading state.
        { loading: RouteLoading.None }
    )

    useRouteEffect(AppRoutes.ApplicationTypeCreate, () => {
        // The form needs organizations to assign one to the new application type.
        return loadOrganizations(provider, appState)
    })

    useRouteEffect(AppRoutes.GetSDK, async () => {
        // Refresh the current user so newly assigned application types show up.
        await provider.getViewModel(SessionViewModel).getCurrentUser()
        await loadOrganizations(provider, appState)
    })

    useRouteEffect(AppRoutes.AllUsers, async () => {
        // User data depends on the organizations being loaded first.
        await loadOrganizations(provider, appState)
        await loadUsers(provider, appState)
    })

    useRouteEffect(AppRoutes.UserDetail, async (params) => {
        const userIdStr = params?.id
        const userId = Number(userIdStr)

        if (!userIdStr || Number.isNaN(userId)) return

        await Promise.all([
            loadUser(provider, userId),
            loadAppTypesForUser(provider, userId),
            loadOrganizations(provider, appState)
        ])
    })

    useRouteEffect(AppRoutes.Application, async (params, urlSearchParams) => {
        if (!params?.id || !urlSearchParams) return
        const appId = getAppTypeIdFromParams(params)
        const status = getStatusFromUrl(urlSearchParams)

        if (!appId || !status) return

        const flags = getFlagsFromUrl(urlSearchParams)
        const page = getPageFromUrl(urlSearchParams)

        const appVM = provider.getViewModel(ApplicationViewModel)
        await appVM.getApplications(appId, status, flags, page)
    })

    useRouteEffect(
        AppRoutes.ApplicationDetail,
        (params?: Params) => {
            return loadApplicationDetail(provider, params?.id ?? null, appState)
        },
        // loadApplicationDetail owns the loading state.
        { loading: RouteLoading.None }
    )

    useRouteEffect(AppRoutes.ApplicationSearch, (params?: Params) => {
        return searchApplication(provider, params?.query ?? null, navigate)
    })

    useRouteEffect(AppRoutes.ApkDetail, (params?: Params) => {
        return loadApkDetail(provider, Number(params?.id))
    })

    return null
}

async function loadDashboard(provider: ViewModelContainer, appState: AppState): Promise<void> {
    // for the dashboard we need
    // 1. application types
    appState.setRouteLoading(true)
    const appTypeVM = provider.getViewModel(ApplicationTypeViewModel)
    appState.setRouteLoading(false)

    const currentUser = provider.getViewModel(SessionViewModel).currentUser
    // 2. logged in user
    if (currentUser) {
        // 3. dashboard data for enabled apps
        await appTypeVM.getThreatsOverview(currentUser.preferences.enabledApps)
    } else {
        forceRedirect404()
    }
}

interface LoadOrganizationsOptions {
    /**
     * Refetches the organization list even when it is already loaded.
     *
     * Use this when the route displays the list; other routes reuse the cached
     * list for context.
     *
     * @default false
     */
    refresh?: boolean
}

async function loadOrganizations(
    provider: ViewModelContainer,
    appState: AppState,
    { refresh = false }: LoadOrganizationsOptions = {}
): Promise<void> {
    const orgVM = provider.getViewModel(OrganizationViewModel)
    const sessionVM = provider.getViewModel(SessionViewModel)

    if (hasSuperAdminAccess(appState.roles)) {
        // Superadmins can load the full organization list.
        await (refresh ? orgVM.getOrganizations() : orgVM.ensureOrganizations())
        // Select the only organization automatically; otherwise let the user choose.
        if (orgVM.organizations.length === 1) {
            orgVM.setSelectedOrgId(orgVM.organizations[0].id)
        }
    } else {
        // Other users can access only their own organization.
        const orgId = sessionVM.currentUserOrgId
        if (orgId) {
            // This also sets `selectedOrgId`. Always fetch the detail because the
            // shared store may already contain a list entry without credentials.
            await orgVM.getOrganization(orgId)
        }
    }
}

async function loadUsers(provider: ViewModelContainer, appState: AppState): Promise<void> {
    const orgVM = provider.getViewModel(OrganizationViewModel)
    const userListVM = provider.getViewModel(UserListViewModel)
    const logger = provider.getViewModel(LoggerViewModel)

    const { roles } = appState
    const { selectedOrgId } = orgVM

    if (hasSuperAdminAccess(roles)) {
        // 1. superadmin - can see all users in all orgs
        if (selectedOrgId && selectedOrgId !== "none") {
            await userListVM.getUsers(selectedOrgId)
        } else {
            // setting "none" will cause showing select org dialog in All users view
            orgVM.setSelectedOrgId("none")
        }
    } else if (hasOrgAdminAccess(roles)) {
        // 2. Org admin - can see users only in their org
        if (selectedOrgId) {
            await userListVM.getUsers(selectedOrgId)
        } else {
            logger.logErrorMessage("Organization ID is not set.")
        }
    }
}

async function loadUser(provider: ViewModelContainer, userId: number): Promise<void> {
    await provider.getViewModel(UserDetailViewModel, userId).getUserDetail()
}

async function loadAppTypesForUser(provider: ViewModelContainer, userId: number): Promise<void> {
    const vm = provider.getViewModel(UserAppTypeViewModel)
    await vm.getUserAppTypes(userId)
}

async function loadApplicationTypeDetail(
    provider: ViewModelContainer,
    appState: AppState,
    appTypeId: number | null
): Promise<void> {
    if (!appTypeId) {
        provider.getViewModel(LoggerViewModel).logErrorMessage("Missing application type id.")
        return
    }

    const vm = provider.getViewModel(ApplicationTypeViewModel)

    // Cached record: render immediately. Cold load: show the route spinner.
    const isColdLoad = vm.appTypeFor(appTypeId) === null
    if (isColdLoad) {
        appState.setRouteLoading(true)
    }

    try {
        // The detail view needs the organization name, while the app type stores only its ID.
        await Promise.all([loadOrganizations(provider, appState), vm.getAppType(appTypeId)])
    } finally {
        if (isColdLoad) {
            appState.setRouteLoading(false)
        }
    }
}

async function loadApplicationDetail(
    provider: ViewModelContainer,
    applicationId: string | null,
    appState: AppState
): Promise<void> {
    if (!applicationId) {
        provider.getViewModel(LoggerViewModel).logErrorMessage("Missing application id.")
        return
    }

    // Render cached detail immediately when available; only show the route
    // spinner while waiting for the first detail response.
    appState.setRouteLoading(true)
    const vm = provider.getViewModel(ApplicationDetailViewModel, applicationId)
    if (vm.applicationDetail) {
        appState.setRouteLoading(false)
        // then continue loading detail, events and flags
    }

    // UUIDs identify devices by `avUserId`; the view model resolves them through
    // the detail endpoint before loading the rest of the device data.
    await vm.getApplicationDetail()
    await vm.getApplicationEvents()
    await vm.getApplicationFlagHistory()
    appState.setRouteLoading(false)
    // Load the non-default tab after the main page is ready.
    await vm.getApplicationApks()
}

async function loadApkDetail(provider: ViewModelContainer, apkId: number): Promise<void> {
    // The route parameter is user-controlled, so reject malformed IDs before loading.
    if (!Number.isInteger(apkId)) {
        provider.getViewModel(LoggerViewModel).logErrorMessage("Invalid APK id.")
        return
    }

    await provider.getViewModel(ApkDetailViewModel, apkId).getApkDetail()
}

async function searchApplication(
    provider: ViewModelContainer,
    query: string | null,
    navigate: NavigateFunction
): Promise<void> {
    if (!query) {
        const logger = provider.getViewModel(LoggerViewModel)
        logger.logErrorMessage("Missing query")
        return
    }

    const vm = provider.getViewModel(ApplicationSearchViewmodel)
    // if we already have data for query in search view model, no need to load them again
    if (vm.hasDataFor(query)) {
        return
    }

    const redirect = await vm.searchApplicationByQuery(query)
    if (redirect) {
        navigate(redirect)
    }
}
